<template>
  <SectionTabHoriz04
    v-if="contextData"
    :title="contextData.title"
    :description="contextData.description"
    :tabs="contextTabs"
    :theme="theme"
  />
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    name: string;
    description: string;
    link: string;
    linkName: string;
    trackingCode?: string;
  }

  interface ISuiteProductData {
    title: string;
    description?: string;
    tabs: {
      tabName: string;
      name: string;
      description: string;
      link: string;
      linkName: string;
      thumbnail: string;
      trackingCode?: string;
      items: IItem[];
    }[];
  }

  interface ISuiteProduct {
    component: 'suite-product';
    meta: {
      navigable?: string;
    };
    data: ISuiteProductData;
  }

  type IProps = ISuiteProps<ISuiteProduct>;

  const props = defineProps<IProps>();

  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);

  const { provideSuiteThumbnailUrl } = useImagePath(props.urlConnector);
  const { provideFormatUrl } = useFormatUrl();

  const contextData = computed((): ISuiteProductData => props.suiteData.data);

  const contextTabs = computed((): ISuiteProductData['tabs'] =>
    contextData.value.tabs.map((tabItem) => ({
      ...tabItem,
      thumbnail: provideSuiteThumbnailUrl(tabItem.thumbnail),
      link: provideFormatUrl(tabItem.link),
      items: tabItem.items.map((item) => ({
        ...item,
        link: provideFormatUrl(item.link),
      })),
    }))
  );

  const theme = computed((): TTheme => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'green';
      case 'connected':
        return 'cyan';
      case 'ai':
        return 'purple';
      case 'operations':
        return 'yellow';
      default:
        return 'blue';
    }
  });

  export type { IItem, ISuiteProduct, ISuiteProductData };
</script>
